import React, { useState, useEffect, useRef, useCallback } from "react";
import { XIcon } from "@heroicons/react/solid";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { useCommunication } from "../hooks/useCommunication";
import { useAuth } from "../context/AuthContext";

const MessageNotification = ({ isOpen, onClose, onNotificationCountChange }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const notificationRef = useRef(null);
  const { user } = useAuth();
  const { conversations, fetchConversations, getUnreadCount, isUserOnline } = useCommunication();

  // Calculate total unread count from conversations
  const totalUnreadCount = conversations.reduce((total, conv) => {
    const conversationId = conv.conversationId || conv._id;
    return total + getUnreadCount(conversationId);
  }, 0);

  // Convert conversations with unread messages to notifications
  const fetchNotifications = useCallback(() => {
    try {
      setLoading(true);
      setError(null);
      
      const conversationNotifications = conversations
        .filter(conv => {
          const conversationId = conv.conversationId || conv._id;
          return getUnreadCount(conversationId) > 0;
        })
        .map(conv => {
          const conversationId = conv.conversationId || conv._id;
          const unreadCount = getUnreadCount(conversationId);
          const otherParticipant = conv.participants.find(p => {
            const participantId = typeof p.userId === 'string' ? p.userId : p.userId?._id;
            return participantId !== user._id && participantId !== user.id;
          });
          
          return {
            _id: conversationId,
            title: `${otherParticipant?.userId?.name || 'Unknown'} sent you ${unreadCount} message${unreadCount > 1 ? 's' : ''}`,
            messageContent: conv.lastMessage?.content || 'New message',
            isRead: false,
            createdAt: conv.lastMessage?.timestamp || conv.updatedAt,
            senderInfo: {
              name: otherParticipant?.userId?.name || 'Unknown',
              userType: otherParticipant?.userId?.userType || 'user'
            },
            conversationInfo: {
              title: conv.title || 'Direct Message',
              type: conv.conversationType || 'direct'
            },
            conversation: conversationId,
            unreadCount
          };
        })
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      setNotifications(conversationNotifications);
      onNotificationCountChange?.(totalUnreadCount);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [conversations, getUnreadCount, user, totalUnreadCount, onNotificationCountChange]);

  // Update notifications when conversations change
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Listen for real-time updates
  useEffect(() => {
    const handleNewMessage = () => {
      fetchConversations();
      fetchNotifications();
    };

    const handleMessageRead = () => {
      fetchConversations();
      fetchNotifications();
    };

    // Listen to the same events as CommunicationCenter
    if (typeof window !== 'undefined' && window.socketService) {
      window.socketService.on('newMessage', handleNewMessage);
      window.socketService.on('messageRead', handleMessageRead);
      
      return () => {
        window.socketService.off('newMessage', handleNewMessage);
        window.socketService.off('messageRead', handleMessageRead);
      };
    }
  }, [fetchConversations, fetchNotifications]);

  // ------------------ Modal Open Effect ------------------ //
  useEffect(() => {
    if (isOpen) {
      fetchConversations();
      fetchNotifications();
    }
  }, [isOpen, fetchConversations, fetchNotifications]);

  // ------------------ Click Outside ------------------ //
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) onClose();
    };

    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  // ------------------ Handlers ------------------ //
  const handleMarkAsRead = async (conversationId) => {
    // This would mark messages in the conversation as read
    // For now, just remove from notifications
    setNotifications((prev) => prev.filter(n => n._id !== conversationId));
    onNotificationCountChange?.(Math.max(0, totalUnreadCount - 1));
  };

  const handleMarkAllAsRead = async () => {
    // This would mark all messages as read
    // For now, just clear notifications
    setNotifications([]);
    onNotificationCountChange?.(0);
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "";
    const date = new Date(ts);
    const diffMins = Math.floor((new Date() - date) / (1000 * 60));
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    if (diffMins < 10080) return `${Math.floor(diffMins / 1440)}d ago`;
    return format(date, "MMM dd, yyyy");
  };

  const getConversationIcon = (info) => (info?.type === "group" ? "👥" : "💬");

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div ref={notificationRef} className="bg-[#1a1a1a] rounded-lg w-full max-w-md max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h3 className="text-lg font-medium text-white">Message Notifications</h3>
          <div className="flex items-center space-x-2">
            {totalUnreadCount > 0 && (
              <button onClick={handleMarkAllAsRead} className="text-xs text-[#ffca20] hover:text-yellow-400">
                Mark all read
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <XIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Notifications */}
        <div className="flex-1 overflow-y-auto">
          {loading && notifications.length === 0 ? (
            <div className="p-4 text-center text-gray-400">Loading notifications...</div>
          ) : error ? (
            <div className="p-4 text-center text-red-400">{error}</div>
          ) : notifications.length === 0 ? (
            <div className="p-4 text-center text-gray-400">
              <div className="text-4xl mb-2">💬</div>
              <p>No message notifications</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-700">
              {notifications.map((n) => {
                const hasOnlineUser = n.conversation && conversations.find(conv => {
                  const convId = conv.conversationId || conv._id;
                  return convId === n.conversation;
                })?.participants.some(p => {
                  const participantId = p.userId?._id || p.userId;
                  const currentUserId = user._id || user.id;
                  const isOtherUser = participantId !== currentUserId;
                  return isOtherUser && isUserOnline && isUserOnline(participantId);
                });

                return (
                  <div
                    key={n._id}
                    className="p-4 hover:bg-[#2a2a2a] transition-colors cursor-pointer bg-[#2a2a2a]/50 border-l-4 border-l-[#ffca20]"
                    onClick={() => handleMarkAsRead(n._id)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-[#ffca20] rounded-full flex items-center justify-center">
                        <span className="text-lg">{getConversationIcon(n.conversationInfo)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <h4 className="text-sm font-medium truncate text-white">
                              {n.title}
                            </h4>
                            {hasOnlineUser && (
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            )}
                            {n.unreadCount > 0 && (
                              <span className="bg-[#ffca20] text-black text-xs px-2 py-1 rounded-full font-bold">
                                {n.unreadCount > 99 ? "99+" : n.unreadCount}
                              </span>
                            )}
                          </div>
                          <div className="w-2 h-2 bg-[#ffca20] rounded-full flex-shrink-0"></div>
                        </div>
                        <p className="text-sm mt-1 truncate text-gray-300">
                          {n.messageContent}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-500">{n.senderInfo?.name || "Unknown"}</span>
                          <span className="text-xs text-gray-500">{formatTimestamp(n.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}


            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 text-xs text-gray-500 text-center">
          {notifications.length > 0 && (
            <p>
              Showing {notifications.length} conversation{notifications.length !== 1 ? "s" : ""} with unread messages
              {totalUnreadCount > 0 && ` • ${totalUnreadCount} total unread`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageNotification;
